import { useVirtualListScrollRestore } from '@/hooks/use-virtual-list-scroll-restore';
import { useUnit } from 'effector-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { LogoLoader } from '@/components/logo-loader';
import {
	$addDeviceLogs,
	$cursorDeviceLogs,
	$hasMoreDeviceLogs,
	$isFetchingDeviceLogs,
	$deviceLogs,
	$setCursorDeviceLogs,
	$setHasMoreDeviceLogs,
	$setIsFetchingDeviceLogs,
	$setDeviceLogs,
	type IDeviceLog,
} from '@/entities/device-log.entity';
import {
	$clientsFilter,
	$clientsSearchText,
	$clientsSortDir,
	$deviceLogsFetchKey,
	$deviceLogsListScrollTop,
	$deviceLogsSearchField,
	$deviceLogsSortBy,
	$setDeviceLogsFetchKey,
	$setDeviceLogsListScrollTop,
	onlineStatusToIsOnline,
	toServerDeviceLogsFilters,
} from '@/entities/clients-filter.entity';
import { isDevMode } from '@/lib/is-dev-mode';
import { mockGlobalDeviceLogs } from '@/mocks/fixtures';
import DeviceLogCard from '../cards/device-log-card';
import { BugIcon } from 'lucide-react';

const LIMIT = 35;
const SCROLL_COMPENSATION_THRESHOLD = 80;

export default function DeviceLogsList() {
	const listRef = useRef<HTMLDivElement | null>(null);
	const prevCountRef = useRef(0);
	const prevFirstIdRef = useRef<number | null>(null);

	const [deviceLogs, addDeviceLogs, setDeviceLogs] = useUnit([$deviceLogs, $addDeviceLogs, $setDeviceLogs]);
	const [hasMore, setHasMore] = useUnit([$hasMoreDeviceLogs, $setHasMoreDeviceLogs]);
	const [cursor, setCursor] = useUnit([$cursorDeviceLogs, $setCursorDeviceLogs]);
	const [isFetching, setIsFetching] = useUnit([$isFetchingDeviceLogs, $setIsFetchingDeviceLogs]);
	const [filter, searchText, searchField, sortBy, sortDir, savedScrollTop, setScrollTop, fetchKey, setFetchKey] = useUnit([
		$clientsFilter,
		$clientsSearchText,
		$deviceLogsSearchField,
		$deviceLogsSortBy,
		$clientsSortDir,
		$deviceLogsListScrollTop,
		$setDeviceLogsListScrollTop,
		$deviceLogsFetchKey,
		$setDeviceLogsFetchKey,
	]);
	const [firstFetched, setFirstFetched] = useState(isDevMode);

	const isOnline = onlineStatusToIsOnline(filter.onlineStatus);
	const serverFilters = useMemo(() => toServerDeviceLogsFilters(filter), [filter]);
	const currentFetchKey = `${searchField}:${searchText}:${filter.onlineStatus}:${JSON.stringify(serverFilters)}:${sortBy}:${sortDir}`;

	const queryPayload = useMemo(
		() => ({
			limit: LIMIT,
			searchText: searchText || undefined,
			searchField,
			isOnline,
			filters: serverFilters,
			sortBy,
			sortDir,
		}),
		[searchText, searchField, isOnline, serverFilters, sortBy, sortDir],
	);

	const virtualizer = useVirtualizer({
		count: deviceLogs.length,
		getScrollElement: () => listRef.current,
		estimateSize: () => 160,
		overscan: 8,
		measureElement: el => el.getBoundingClientRect().height,
		gap: 8,
	});
	const virtualItems = virtualizer.getVirtualItems();

	useVirtualListScrollRestore({
		listType: 'deviceLogs',
		listRef,
		virtualizer,
		items: deviceLogs,
		savedScrollTop,
		setScrollTop,
		ready: firstFetched && deviceLogs.length > 0,
	});

	useEffect(() => {
		if (isDevMode) {
			setDeviceLogs(mockGlobalDeviceLogs);
			setHasMore(false);
			return;
		}

		function addDeviceLogsHandler(data: { deviceLogs: IDeviceLog[]; cursor: string; hasMore: boolean }) {
			setIsFetching(false);
			if (!firstFetched) setFirstFetched(true);
			if (data?.deviceLogs?.length) {
				addDeviceLogs(data.deviceLogs);
				setCursor(data.cursor);
			}
			setHasMore(data.hasMore);
		}

		socket.on(SOCKET_EVENTS.addDeviceLogs, addDeviceLogsHandler);

		return () => {
			socket.off(SOCKET_EVENTS.addDeviceLogs, addDeviceLogsHandler);
		};
	}, []);

	useEffect(() => {
		if (isDevMode) {
			setFirstFetched(true);
			return;
		}

		function fetch() {
			setIsFetching(true);
			setDeviceLogs([]);
			setCursor(null);
			setScrollTop(0);
			setFetchKey(currentFetchKey);
			socket.emit(SOCKET_EVENTS.getDeviceLogs, { ...queryPayload, cursor: null });
		}

		if (deviceLogs.length > 0 && fetchKey === currentFetchKey) {
			setFirstFetched(true);
			return;
		}

		if (!firstFetched) {
			fetch();
		} else {
			const timeout = setTimeout(fetch, 250);
			return () => clearTimeout(timeout);
		}
	}, [currentFetchKey]);

	useLayoutEffect(() => {
		const firstId = deviceLogs[0]?.id ?? null;
		const isPrepend =
			deviceLogs.length === prevCountRef.current + 1 && prevCountRef.current > 0 && firstId !== prevFirstIdRef.current;

		if (isPrepend && listRef.current) {
			const el = listRef.current.querySelector('[data-index="0"]') as HTMLElement | null;
			const height = el?.getBoundingClientRect().height ?? 160;

			if (listRef.current.scrollTop > SCROLL_COMPENSATION_THRESHOLD) {
				listRef.current.scrollTop += height + 8;
			}
		}

		prevCountRef.current = deviceLogs.length;
		prevFirstIdRef.current = firstId;
	}, [deviceLogs]);

	useEffect(() => {
		if (!hasMore || isFetching) return;

		if (virtualItems.length > 0) {
			const lastItem = virtualItems[virtualItems.length - 1];
			if (lastItem.index >= deviceLogs.length - 10) {
				if (socket.connected) {
					setIsFetching(true);
					socket.emit(SOCKET_EVENTS.getDeviceLogs, { ...queryPayload, cursor });
				}
			}
		}
	}, [virtualItems, deviceLogs.length, isFetching, hasMore, cursor, queryPayload]);

	return (
		<div ref={listRef} data-scroll-container className='h-full overflow-auto pr-1 px-1'>
			{!firstFetched || (isFetching && !deviceLogs.length) ? (
				<div className='flex items-center justify-center mt-30'>
					<LogoLoader />
				</div>
			) : deviceLogs.length === 0 ? (
				<div className='flex flex-col items-center justify-center mt-30 gap-2 text-center'>
					<BugIcon size={24} className='text-muted-foreground/40' />
					<p className='text-md text-muted-foreground'>Ошибок пока нет</p>
				</div>
			) : (
				<div
					style={{
						height: `${virtualizer.getTotalSize()}px`,
						width: '100%',
						position: 'relative',
					}}
				>
					<div
						style={{
							position: 'absolute',
							top: 0,
							left: 0,
							width: '100%',
							transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
						}}
					>
						{virtualItems.map(({ index, key }) => {
							const deviceLog = deviceLogs[index];
							return (
								<div key={key} data-index={index} ref={virtualizer.measureElement} className='pb-2'>
									<DeviceLogCard deviceLog={deviceLog} />
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
}
